import React from 'react';
import classNames from 'classnames';

import Data, { _F } from 'data';
import Store from 'store';

import { getIconPath } from 'common';
import { keyedList } from 'utils';
import { formatHtml } from 'utils/format';
import { ColorNumber, ItemIcon } from './common';

const shipTypes = {
  1: 'tip.name.enum_shiptype_pirate',
  2: 'tip.name.enum_shiptype_poongbaek',
  3: 'tip.name.enum_shiptype_estoc',
  4: 'tip.name.enum_shiptype_icebreaker',
  5: 'tip.name.enum_shiptype_ghost',
  6: 'tip.name.enum_shiptype_multi',
  7: 'tip.name.enum_shiptype_steel',
  8: 'tip.name.enum_shiptype_sloop',
};

const toleranceTypes = {
  seaweed: 'sys.tooltip.voyage_crew_tolerance_seaweed',
  sandstorm: 'sys.tooltip.voyage_crew_tolerance_sandstorm',
  siren: 'sys.tooltip.voyage_crew_tolerance_siren',
  coldwave: 'sys.tooltip.voyage_crew_tolerance_coldwave',
  storm: 'sys.tooltip.voyage_crew_tolerance_storm',
  deadperson: 'sys.tooltip.voyage_crew_tolerance_deadperson',
};

const townCrewGrades = [
  'tip.name.enum_towndispatchcrewgrade_common',
  'tip.name.enum_towndispatchcrewgrade_uncommon',
  'tip.name.enum_towndispatchcrewgrade_rare',
  'tip.name.enum_towndispatchcrewgrade_epic',
  'tip.name.enum_towndispatchcrewgrade_legend',
  'tip.name.enum_towndispatchcrewgrade_relic',
];
const townCrewHardshipMain = {
  1: 'tip.name.hardship_main_quake',
  2: 'tip.name.hardship_main_thorn',
  3: 'tip.name.hardship_main_lapidification',
  4: 'tip.name.hardship_main_poison',
};
const townCrewHardshipSub = {
  1: 'tip.name.hardship_sub_darkness',
  2: 'tip.name.hardship_sub_ice',
  3: 'tip.name.hardship_sub_wind',
  4: 'tip.name.hardship_sub_fire',
};

export function formatStat(info, value) {
  if (info.decimal) {
    value = (value / Math.pow(10, info.decimal)).toFixed(info.decimal);
  }
  return (value > 0 ? '+' : '') + value + (info.percent ? '%' : '');
}

function ItemBinding({ item }) {
  const data = Data.items[item.id];
  if (Data.shipAvatar[item.id]) {
    if (data.bindTarget) {
      return <p className="lap-clearfix"><span className="lap-right">{_F('sys.tip.tilte_trade_unable')}</span></p>;
    }
  } else if (item.template) {
    if (data.bindType === 1) {
      if (data.bindTarget === 1) {
        return <p>{_F('tip.name.enum_itembindtype_on_equip')}</p>;
      } else if (data.bindTarget === 2) {
        return <p>{_F('sys.tip.ui_item_account_binds_when_equipped')}</p>;
      }
    } else if (data.bindType === 2) {
      if (data.bindTarget === 1) {
        return <p>{_F('tip.name.enum_itembindtype_on_pickup')}</p>;
      } else if (data.bindTarget === 2) {
        return <p>{_F('sys.tip.ui_item_account_binds_when_picked_up')}</p>;
      }
    }
  } else if (data.bindType) {
    if (data.bindTarget === 1) {
      return <p className="lap-clearfix"><span className="lap-right">{_F('sys.tip.tilte_trade_unable')}</span>{_F('tip.name.common_item_being_picked_up')}</p>;
    } else if (data.bindTarget === 2) {
      return <p className="lap-clearfix"><span className="lap-right">{_F('sys.tip.tilte_trade_unable')}</span>{_F('sys.tip.ui_item_account_binded')}</p>;
    }
  }
  return null;
}

const compareStats = (a, b) => (Data.itemStats[a[0]]?.id || 0) - (Data.itemStats[b[0]]?.id || 0);

function ItemStaticStats({ item }) {
  const data = Data.items[item.id];
  let stats = null;
  if (data.abilityStone) {
    stats = data.abilityStone.stats;
  } else {
    const base0 = data.static0 && Data.itemStatic[`${data.static0}#${data.grade}`];
    const base1 = data.static1 && Data.itemStatic[`${data.static1}#${data.grade}`];
    const upgrade = Data.itemQuality[`${data.quality}#${100 + item.upgrade}`];
    stats = {};
    for (const stat of Object.keys(Data.itemStats)) {
      const value = (base0?.stats[stat] || 0) + (base1?.stats[stat] || 0) + (upgrade?.stats[stat] || 0);
      if (value) stats[stat] = value;      
    }
  }
  if ((!stats || !Object.keys(stats).length)) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('tip.name.common_stats_equip_basic')}
      </div>
      {!!stats && Object.entries(stats).sort(compareStats).map(([stat, value]) => {
        const info = Data.itemStats[stat];
        if (!info) return null;
        return (
          <div key={stat}>{_F(info.tip)} {formatStat(info, value)}</div>
        );
      })}
    </div>
  );
}

function ItemBonusStats({ item }) {
  const data = Data.items[item.id];
  if (!item.engraving || !data.abilityStone) return null;
  const stats = {};
  let total = 0;
  for (const [id, value] of Object.entries(item.engraving)) {
    const info = Data.engraving[id];
    if (info && !info.penalty) total += value;
  }
  const carve = data.abilityStone.carve;
  if (total >= carve.stage2) {
    stats[carve.stat] = carve.value2;
  } else if (total >= carve.stage1) {
    stats[carve.stat] = carve.value1;
  } else if (total >= carve.stage0) {
    stats[carve.stat] = carve.value0;
  }
  if (!stats || !Object.keys(stats).length) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tip.item_property_stepbonus')}
      </div>
      {Object.entries(stats).sort(compareStats).map(([stat, value]) => {
        const info = Data.itemStats[stat];
        if (!info) return null;
        return (
          <div key={stat}>{_F(info.tip)} {formatStat(info, value)}</div>
        );
      })}
    </div>
  );
}

function ItemRandomStats({ item }) {
  const data = Data.items[item.id];
  if (item.template && data.random0) {
    const random = Data.itemRandom[`${data.random0}#${data.grade}`];
    if (!random) return null;
    if (random.length !== 1 || random[0].subgroups.some(s => s.count * 6 !== s.options.length)) {
      const counts = random.map(g => g.subgroups.reduce((sum, s) => sum + s.count, 0));
      const cMin = Math.min(...counts), cMax = Math.max(...counts);
      return (
        <div className="lap-item-group">
          <div className="lap-stats-header">
            {_F('tip.name.item_random_option_title')}
          </div>
          {cMin === cMax ? (
            <div>{_F('sys.tooltip.item_property_unchecked_same', cMax)}</div>
          ) : (
            <div>{_F('sys.tooltip.item_property_unchecked', cMin, cMax)}</div>
          )}
        </div>
      );
    }
    const stats = [];
    for (const group of random) {
      for (const sub of group.subgroups) {
        for (let i = 0; i < sub.options.length; i += 6) {
          const [type, stat, value, dev] = sub.options.slice(i, i + 6);
          const formatValue = info => dev
            ? _F('sys.itemreforging.range_valuable', formatStat(info, value - dev), formatStat(info, value))
            : formatStat(info, value);
          let info;
          switch (type) {
          case 2:
            info = Data.itemStats[Data.statsMap[stat]];
            if (info) stats.push(<>{_F(info.tip)} {formatValue(info)}</>);
            break;
          case 3:
            info = Data.engraving[stat];
            if (info) stats.push(_F(info.penalty ? 'sys.tooltip.item_engrave_option_negative' : 'sys.tooltip.item_engrave_option', info.name,
              dev ? _F('sys.itemreforging.range_valuable', value - dev, value) : value));
            break;
          case 4:
            // combateffect
            info = Data.combatEffect[stat];
            if (info) stats.push(formatHtml(info));
            break;
          case 28:
            // feature
            info = Data.featureTypes[Data.abilityFeature[stat]?.type];
            if (info) stats.push(_F(info.tip, formatStat(info, Data.abilityFeature[stat].value)));
            break;
          default:
            info = Data.statTypes[type];
            if (info) {
              const values = [formatValue(info)];
              if (info.prepend) values.unshift(_F(info.prepend[stat]));
              stats.push(_F(info.tip, ...values));
            }
          }
        }
      }
    }
    if (!stats.length) return null;
    return (
      <div className="lap-item-group">
        <div className="lap-stats-header">
          {_F('sys.tooltip.item_random_option_title')}
        </div>
        {stats.map((stat, index) => <div key={index}>{stat}</div>)}
      </div>
    );
  }
  if (!item.quality || !Object.keys(item.quality).length) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.item_random_option_title')}
      </div>
      {Object.entries(item.quality).sort(compareStats).map(([stat, value]) => {
        const info = Data.itemStats[stat];
        if (!info) return null;
        if (typeof value === 'object') {
          value = _F('sys.itemreforging.range_valuable', formatStat(info, value.min), formatStat(info, value.max));
        } else {
          value = formatStat(info, value);
        }
        return <div key={stat}>{_F(info.tip)} {value}</div>;
      })}
    </div>
  );
}

function ItemTripod({ item }) {
  const skills = Store.useBuilder(builder => builder?.skills);

  const data = Data.items[item.id];
  const base = Data.itemStatic[`${data.static0}#${data.grade}`];
  if (!base) return null;
  if (item.template && base.tripod) {
    return (
      <div className="lap-item-group">
        <div className="lap-stats-header">
          {_F('sys.tooltip.item_tripod_title')}
        </div>
        <ul>
          <li>{_F('sys.tooltip.item_tripod_unchecked_same', Data.tripodOptions[base.tripod]?.count)}</li>
        </ul>
      </div>
    );
  }
  if (!item.tripod || !Object.keys(item.tripod).length) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.item_tripod_title')}
      </div>
      <ul>
        {Object.entries(item.tripod).map(([id, value]) => {
          const [skid, index] = id.split('#');
          const skill = Data.skills[skid];
          const tripod = skill?.tripods?.[parseInt(index) - 1]
          if (!tripod) return null;
          const equipped = skills?.[skid]?.tripods?.[index];
          return (
            <li key={id} className={equipped ? "lap-equipped-tripod" : null}>
              {!!Data.playerClasses[skill.class] && _F('sys.tooltip.class_of_skill', _F(Data.playerClasses[skill.class].name))}
              {_F('sys.tooltip.item_tripod_option', skill.name, tripod.name, value)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ItemRestrictions({ item }) {
  const data = Data.items[item.id];
  const entries = [];
  if (data.noSell) {
    entries.push(_F('tip.name.common_npc_sell_impossible'));
  }
  if (data.noDestroy) {
    entries.push(_F('tip.name.common_items_impossible_destroy'));
  }
  if (data.noDisassembly || item.upgrade) {
    entries.push(_F('sys.tip.item_disable_dissemble'));
  }
  if (!data.upgrade && (data.category >= 10000 && data.category < 20000 && data.category !== 10500)) {
    entries.push(_F('sys.calibrate.tooltip_upgrade_disabled'));
  }
  if (data.equipDenominator && data.battleStack) {
    entries.push(_F('sys.tip.ui_battlestack', data.battleStack));
  }
  if (!entries.length) return null;
  const result = [];
  for (const entry of entries) {
    if (result.length) result.push(', ');
    result.push(entry);
  }
  return <p>{keyedList(result)}</p>;
}

function ItemSources({ id }) {
  const data = Data.items[id];
  if (!data || !data.sources) return null;
  return (
    <div className="lap-item-group">
      {data.sources.map((src, index) => <div key={index}>{_F('sys.dic.acquisition_string_combination', src)}</div>)}
    </div>
  );
}

function ItemDurability({ item }) {
  const data = Data.items[item.id];
  if (!item.template && data.maxDurability > 100) {
    const durability = Math.round(data.maxDurability / 100);
    return <p>{_F('tip.name.common_durability_1')} {durability} / {durability}</p>;
  }
  return null;
}

function ItemSetEffects({ set, equippedPieces=6, equippedPiecesLv2=0 }) {
  const effects = [];
  for (const fx of set.effects) {
    const dst = effects[fx.count] = (effects[fx.count] || {levels: 1, desc: []});
    dst.levels = Math.max(dst.levels, fx.level);
    dst.level = (equippedPiecesLv2 >= fx.count ? 2 : (equippedPieces >= fx.count ? 1 : 0));
    if (fx.level === (equippedPiecesLv2 >= fx.count ? 2 : 1)) {
      dst.desc.push(fx.desc);
    }
  }

  const setLevel = lvl => _F('sys.tooltip.set_upgrade_level_grade', lvl);

  return effects.map(({level, levels, desc}, count) => (
    <div key={count} className={classNames({"lap-disabled": !level})}>
      <div className="lap-itemset-title">
        {_F('tip.name.itemset_title', count, (levels > 1 ? <>
          <span className={level === 1 ? "lap-active" : "lap-inactive"}>{setLevel(1)}</span>
          {" / "}
          <span className={level === 2 ? "lap-active" : "lap-inactive"}>{setLevel(2)}</span>
        </> : (
          <span className={level ? "lap-active" : "lap-inactive"}>{setLevel(1)}</span>
        )))}
      </div>
      <ul className="lap-bullet">
        {desc.map((line, idx) => <li key={idx}>{formatHtml(line)}</li>)}
      </ul>
    </div>
  ));
}

function ItemSet({ item }) {
  const items = Store.useSelector(state => {
    const builder = state.builder;
    if (builder && builder.items && Object.values(builder.items).some(Boolean)) return builder.items;
    const embed = state.embeds?.[state.activeEmbed];
    if (embed && embed.items && Object.values(embed.items).some(Boolean)) return embed.items;
  });
  const data = Data.items[item.id];
  const set = Data.itemSet[data.setIndex];
  if (!set || !set.items) return null;

  let equippedPieces = 0, equippedPiecesLv2 = 0;
  const equippedCategories = {};
  if (items) {
    for (const item of Object.values(items)) {
      const idata = item && Data.items[item.id];
      if (idata && idata.setIndex === data.setIndex) {
        equippedPieces += 1;
        if (idata.setLevel > 1) {
          equippedPiecesLv2 += 1;
        }
        equippedCategories[idata.category] = idata.setLevel || 1;
      }
    }
  }

  return <>
    {!!set.name && (
      <div className="lap-item-group">
        <div>{_F('sys.tip.setoption_title', set.name)}</div>
        <ul>
          {set.items.map(id => (
            <li key={id} className={classNames({"lap-disabled": !equippedCategories[id]})}>
              {_F('sys.tip.setoption_desc', set.name, _F(Data.itemCategory[id]?.name))}
              {!!equippedCategories[id] && _F('sys.tip.setoption_desc_grade', equippedCategories[id])}
            </li>
          ))}
        </ul>
      </div>
    )}
    <ItemSetEffects set={set} equippedPieces={equippedPieces} equippedPiecesLv2={equippedPiecesLv2}/>
  </>;
}

function itemGetQuality(item) {
  const data = Data.items[item.id];
  const random = Data.itemRandom[`${data.random0}#${data.grade}`];
  if (!random || !item.quality) return 100;
  const count = Object.keys(item.quality).length;
  if (!count) return 100;
  for (const group of random) {
    let stats = {...item.quality};
    let sum = 0;
    let valid = true;
    for (const sub of group.subgroups) {
      let count = sub.count;
      for (let i = 0; i < sub.options.length && count; i += 6) {
        const [type, stat, value, dev, amp] = sub.options.slice(i, i + 6);
        if (type === 2 && stats[Data.statsMap[stat]] && stats[Data.statsMap[stat]] >= value - dev && stats[Data.statsMap[stat]] <= value) {
          if (dev) {
            let q = (stats[Data.statsMap[stat]] - (value - dev)) / dev;
            if (amp) q = Math.pow(q, 1 / amp);
            sum += q;
          } else {
            sum += 1;
          }
          delete stats[Data.statsMap[stat]];
          count -= 1;
        }
      }
      if (count) {
        valid = false;
        break;
      }
    }
    if (valid && Object.keys(stats).length === 0) {
      return Math.max(0, Math.min(100, Math.floor(100 * sum / count)));
    }
  }
  return 100;
}

function ItemQualityBar({ item }) {
  const data = Data.items[item.id];
  if (data.category < 10000 || data.category >= 10500) return null;
  if (item.template && data.random0) {
    return (
      <div className="lap-item-quality">
        {_F('sys.tooltip.item_calibrate_title')}
        {_F('sys.tooltip.item_calibrate_unchecked')}
      </div>
    );
  }
  if (!item.quality) return null;
  const quality = itemGetQuality(item);
  const tier = [0, 9, 29, 69, 89, 99, 100].findIndex(v => v >= quality);
  return (
    <div className="lap-item-quality">
      {_F('sys.tooltip.item_calibrate_title')}
      <div className={`lap-item-quality-bar lap-quality-${tier}`}>
        <span>{quality}</span>
        <span className="lap-quality-bg">
          <span className="lap-quality-fg" style={{width: `${quality}%`}}/>
        </span>
      </div>
    </div>
  );
}

function formatTime(time) {
  if (time < 60 * 1000) {
    return _F('tip.name.gfx_secs', Math.round(time / 1000));
  } else if (time < 60 * 60 * 1000) {
    return _F('tip.name.gfx_minutes', Math.round(time / 60 / 1000));
  } else if (time < 24 * 60 * 60 * 1000) {
    return _F('tip.name.gfx_hours', Math.round(time / 60 / 60 / 1000));
  } else {
    return _F('tip.name.gfx_days', Math.round(time / 24 / 60 / 60 / 1000));
  }
}

export const showItemLevel = cat => (cat >= 10101 && cat <= 10205) || (cat >= 30700 && cat <= 30702);

function ItemHeader({ item }) {
  const data = Data.items[item.id];
  const upgrade = Data.itemQuality[`${data.quality}#${100 + item.upgrade}`];
  return (
    <div className="lap-item-header">
      <ItemIcon item={data} upgrade={item.upgrade}/>
      <div className="lap-item-header-info">
        <div className="lap-item-grade">
          {_F('sys.tip.grade_category', _F(Data.itemGrades[data.grade].name))}
        </div>
        {!!(showItemLevel(data.category) && data.ilevel) ? (
          <div className="lap-item-level">
            {_F('tip.name.tooltip_itemlv', data.ilevel + (upgrade?.level || 0))}
            {_F('tip.name.tooltip_itemlv_tier', data.tier)}
          </div>
        ) : (!!data.tier && (
          <div className="lap-item-level">
            {_F('tip.name.tooltip_itemtier', data.tier)}
          </div>
        ))}
        <ItemQualityBar item={item}/>
        {!!data.cooltime && (
          <div className="lap-item-cooldown">
            {_F('sys.tip.useup_cooltime', formatTime(data.cooltime))}
          </div>
        )}
      </div>
    </div>
  );
}

function ItemEngraving({ item }) {
  const stats = item.engraving || {};
  const data = Data.items[item.id];
  if (item.template && data.random1) {
    const random = Data.itemRandom[`${data.random1}#${data.grade}`];
    if (!random) return null;
    if (random.length !== 1 || random[0].subgroups.some(s => s.count * 6 !== s.options.length)) {
      const counts = random.map(g => g.subgroups.reduce((sum, s) => sum + s.count, 0));
      const cMin = Math.min(...counts), cMax = Math.max(...counts);
      return (
        <div className="lap-item-group">
          <div className="lap-stats-header">
            {_F('sys.tooltip.item_engrave_title')}
          </div>
          {cMin === cMax ? (
            <div>{_F('sys.tooltip.item_engrave_unchecked_same', cMax)}</div>
          ) : (
            <div>{_F('sys.tooltip.item_engrave_unchecked', cMin, cMax)}</div>
          )}
        </div>
      );
    }
    for (const group of random) {
      for (const sub of group.subgroups) {
        for (let i = 0; i < sub.options.length; i += 6) {
          const [type, stat, value, dev] = sub.options.slice(i, i + 6);
          if (type === 3) {
            stats[stat] = dev ? {min: value - dev, max: value} : value;
          }
        }
      }
    }
  }
  if (!Object.keys(stats).length) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.item_engrave_title')}
      </div>
      {Object.entries(stats).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([stat, value]) => {
        const info = Data.engraving[stat];
        if (!info) return null;
        if (typeof value === 'object') {
          value = _F('sys.itemreforging.range_valuable', value.min, value.max);
        }
        return <div key={stat}>{_F(info.penalty ? 'sys.tooltip.item_engrave_option_negative' : 'sys.tooltip.item_engrave_option', info.name, value)}</div>;
      })}
    </div>
  );
}

function ItemBracelet({ item }) {
  const data = Data.items[item.id];

  const bracelet = data?.bracelet;
  const [minEffects, maxEffects] = React.useMemo(() => {
    if (!bracelet) return [0, 0];
    let min = 0, max = 0;
    if (bracelet.fixed2) max += 2;
    else if (bracelet.fixed1) max += 1;
    if (bracelet.fixed0) min += 0;
    else if (bracelet.fixed1) min += 1;
    else if (bracelet.fixed2) min += 2;
    if (bracelet.enchant3) max += 3;
    else if (bracelet.enchant2) max += 2;
    else if (bracelet.enchant1) max += 1;
    if (bracelet.enchant1) min += 1;
    else if (bracelet.enchant2) min += 2;
    else if (bracelet.enchant3) min += 3;
    return [min, max];
  }, [bracelet]);

  if (!bracelet) return null;

  if (item.template) {
    return (
      <div className="lap-item-group">
        <div className="lap-stats-header">
          {_F('sys.tooltip.item_bracelet_special_title')}
        </div>
        <div>
          <div>{_F('sys.tooltip.item_bracelet_special_unchecked', minEffects, maxEffects)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.item_bracelet_special_title')}
      </div>
      {!!item.bracelet && item.bracelet.filter(Boolean).map((stats, index) => Object.entries(stats).sort(compareStats).map(([stat, value]) => {
        const info = Data.itemStats[stat];
        if (info) {
          return <div key={`${index}.${stat}`}>{_F(info.tip)} {formatStat(info, value)}</div>;
        } else if (Data.combatEffect[stat]) {
          return <div key={`${index}.${stat}`}>{formatHtml(Data.combatEffect[stat])}</div>;
        }
      }))}
    </div>
  );
}

function ItemEngravingExtra({ item }) {
  if (!item.engraving_extra || !Object.keys(item.engraving_extra).length) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.item_engrave_title_additional')}
      </div>
      {Object.entries(item.engraving_extra).map(([stat, value]) => {
        const info = Data.engraving[stat];
        if (!info) return null;
        return <div key={stat}>{_F('sys.tooltip.item_engrave_option_additional', info.name, value)}</div>;
      })}
    </div>
  );
}

export function gemEffect(index, skill) {
  const groups = Data.itemRandom[index];
  if (!groups) return null;
  for (const group of groups) {
    for (const sub of group.subgroups) {
      for (let i = 0; i < sub.options.length; i += 6) {
        const [type, id, value, , , charClass] = sub.options.slice(i, i + 6);
        if (id === skill) {
          return {type, value, class: charClass};
        }
      }
    }
  }
}

function ItemGem({ gem, skill }) {
  const data = Data.items[gem];
  const effect = data?.gem && gemEffect(`${data.gem.random}#${data.grade}`, skill);
  if (!effect) {
    return (
      <div className="lap-item-gem">
        <div className="lap-item-gem-icon"/>
        <div className="lap-item-gem-effect">
          <p>{_F('sys.tooltip.gem_not_equip')}</p>
        </div>
      </div>
    );
  }
  const value = (effect.value / 100).toFixed(2);
  return (
    <div className="lap-item-gem">
      <div className={`lap-item-gem-icon lap-item-background lap-item-${Data.itemGrades[data.grade].color}`}>
        <span style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>
      </div>
      <div className="lap-item-gem-effect">
        <p className={`la-color-${Data.itemGrades[data.grade].color}`}>{data.name}</p>
        <p>
          {_F('sys.tooltip.class_of_skill', _F(Data.playerClasses[Data.classMap[effect.class]]?.name))}
          {effect.type === 5 && _F('tip.name.skill_damage', Data.skills[skill]?.name, value)}
          {effect.type === 27 && _F('tip.name.skill_cooldown_reduction', Data.skills[skill]?.name, value)}
          {effect.type === 34 && formatHtml(Data.skillGroup[skill]?.desc, value)}
          {effect.type === 35 && formatHtml(Data.skillGroup[skill]?.desc, value)}
        </p>
      </div>
    </div>
  );
}

function ItemGems({ item }) {
  const data = Data.items[item.id];
  if (!data.gemSlots) return null;
  const gems = item.gems?.slice() || [];
  while (gems.length < data.gemSlots) {
    gems.push(null);
  }
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.gem_component_title')}
      </div>
      {gems.map((gem, index) => <ItemGem gem={gem?.[0]} skill={gem?.[1]} key={index}/>)}
    </div>
  );
}

function ItemTitle({ item }) {
  const data = Data.items[item.id];
  return (
    <div className="lap-item-title">
      {item.upgrade ? _F('sys.tip.enhance_item_stage', item.upgrade) : ''}{formatHtml(data.name)}
    </div>
  );
}

function ItemShipSkill({ skill }) {
  return (
    <div className="lap-ship-skill">
      <div className="lap-ship-skill-icon">
        <span style={{backgroundImage: `url(${getIconPath(skill.icon)})`}}/>
      </div>
      <div className="lap-ship-skill-info">
        <p className="lap-ship-skill-name">{_F('sys.voyageskin.skill_name', skill.name, skill.level)}</p>
        <p className={`lap-ship-skill-type lap-ship-skill-type-${skill.type}`}>
          {_F(['tip.name.enum_commonactionusetype_none', 'tip.name.enum_commonactionusetype_passive',
            'tip.name.enum_commonactionusetype_active', 'tip.name.enum_commonactionusetype_toggle'][skill.type])}
        </p>
        <p className="lap-ship-skill-desc">{_F('sys.voyageskin.skill_desc', formatHtml(skill.desc))}</p>
      </div>
    </div>
  );
}

function ItemShipAvatar({ item }) {
  const ship = Data.shipAvatar[item.id];
  return <>
    <p>
      {ship.ships.length === 8 ? _F('sys.voyageskin.equip_shiplist_all') : (
        _F('sys.voyageskin.equip_shiplist', ship.ships.map(id => Data.strings[shipTypes[id]]).join(', '))
      )}
    </p>
    <div className="lap-item-group">
      <div>{formatHtml(ship.desc)}</div>
      <div>{_F('sys.voyageskin.defaultskill_desc')}</div>
    </div>
    {ship.actions.map((skill, index) => <ItemShipSkill key={index} skill={skill}/>)}
  </>;
}

function ItemDescription({ item }) {
  const data = Data.items[item.id];
  if (Data.shipAvatar[item.id]) {
    return <ItemShipAvatar item={item}/>;
  }
  if (data.desc) {
    return (
      <p className="lap-item-description">
        {formatHtml(data.desc)}
      </p>
    );
  }
  return null;
}

function ItemAssembly({ item }) {
  const data = Data.items[item.id];
  const assembly = Data.items[data.assembly];
  if (!assembly) return null;
  return (
    <p>
      {_F('sys.tooltip.town_recipe_desc', assembly.name)}
    </p>
  );
}

function ItemCardArt({ id, awake }) {
  const card = Data.cards[id];
  if (!card) return null;
  return <>
    <div className="lap-item-card-art">
      <img src={getIconPath(card.image)} alt={card.name}/>
      <div className="lap-item-card-border"/>
      {!card.material && (
        <div className="lap-item-card-level">
          {!!awake && <div className={`lap-item-card-awake lap-item-card-awake-${awake}`}/>}
        </div>
      )}
    </div>
  </>;
}

function ItemEngravingBook({ item }) {
  const data = Data.items[item.id];
  if (!data.engraving) return null;
  const engraving = Data.engraving[data.engraving.id];
  if (!engraving) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tip.item_carving')}
      </div>
      <div>
        {_F('sys.tip.item_carving_desc', engraving.name, data.engraving.rank, data.engraving.points)}
      </div>
    </div>
  );
}

function ItemEngravingBookEffect({ item }) {
  const data = Data.items[item.id];
  if (!data.engraving) return null;
  const engraving = Data.engraving[data.engraving.id];
  if (!engraving) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header lap-disabled">
        {_F('sys.tooltip.level_engrave_info')}
      </div>
      {!!engraving.desc[0] && <div>{_F('sys.tooltip.level_engrave_value', 1, 5, engraving.desc[0])}</div>}
      {!!engraving.desc[1] && <div>{_F('sys.tooltip.level_engrave_value', 2, 10, engraving.desc[1])}</div>}
      {!!engraving.desc[2] && <div>{_F('sys.tooltip.level_engrave_value', 3, 15, engraving.desc[2])}</div>}
    </div>
  );
}

function ItemSkillRune({ item }) {
  const rune = Data.runes[item.id];
  if (!rune) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.skillrune_effect')}
      </div>
      <div>
        {formatHtml(rune.desc)}
      </div>
    </div>
  );
}

function ItemGemEffects({ item }) {
  const data = Data.items[item.id];
  if (!data.gem) return null;
  const effect = item.skill && gemEffect(`${data.gem.random}#${data.grade}`, item.skill);
  const value = effect && (effect.value / 100).toFixed(2);
  return <>
    <p>
      {item.skill ? _F('sys.tooltip.gem_item_level', data.gem.level) : _F('sys.tooltip.gem_item_level_unchecked')}
    </p>
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.gem_item_component_title')}
      </div>
      {effect ? (
        <div>
          {_F('sys.tooltip.class_of_skill', _F(Data.playerClasses[Data.classMap[effect.class]]?.name))}
          {effect.type === 5 && _F('tip.name.skill_damage', Data.skills[item.skill]?.name, value)}
          {effect.type === 27 && _F('tip.name.skill_cooldown_reduction', Data.skills[item.skill]?.name, value)}
          {effect.type === 34 && formatHtml(Data.skillGroup[item.skill]?.desc, value)}
          {effect.type === 35 && formatHtml(Data.skillGroup[item.skill]?.desc, value)}
        </div>
      ) : (
        <div>{_F('sys.tooltip.gem_item_option_unchecked')}</div>
      )}
    </div>
  </>;
}

function ItemTooltipCrewTolerance({ type, value }) {
  if (!value) return null;
  return (
    <div>
      <span className={`lap-crew-tolerance-icon lap-crew-${type}`}/>
      {_F(toleranceTypes[type], <span className={value > 0 ? "lap-tolerance-plus" : "lap-tolerance-minus"}>{value > 0 ? '+' : ''}{value}</span>)}
    </div>
  );
}

function ItemTooltipCrew({ item }) {
  const crew = Data.crew[item.id];
  return (
    <div className={`lap-GameTooltip lap-item-${Data.itemGrades[crew.grade].color}`}>
      <div className="lap-item-title">{crew.name}</div>
      <div className="lap-item-body">
        <div className="lap-crew-header">
          <div className="lap-crew-icon">
            <div style={{backgroundImage: `url(${getIconPath(crew.icon)}`}}/>
          </div>
          <div className="lap-crew-header-info">
            <div className="lap-crew-tier">
              <span className="lap-crew-acquisition">{_F('sys.tooltip.target_not_acquired')}</span>
            </div>
            <div className="lap-crew-ship">
              {shipTypes[crew.ship] ? _F('sys.voyage.tooltip_exclusivecrew', crew.tier, _F(shipTypes[crew.ship]))
                : _F('sys.voyage.tooltip_generalcrew', crew.tier)}
            </div>
            <div className="lap-crew-comment">"{formatHtml(crew.comment)}"</div>
          </div>
        </div>
        <div className="lap-crew-story">{formatHtml(crew.story)}</div>
        <div className="lap-crew-speed">{_F('sys.tooltip.voyage_crew_movespeed', (crew.speed / 10).toFixed(1))}</div>
        <div className="lap-crew-tolerance">
          <ItemTooltipCrewTolerance type="seaweed" value={crew.seaweed}/>
          <ItemTooltipCrewTolerance type="sandstorm" value={crew.sandstorm}/>
          <ItemTooltipCrewTolerance type="siren" value={crew.siren}/>
          <ItemTooltipCrewTolerance type="coldwave" value={crew.coldwave}/>
          <ItemTooltipCrewTolerance type="storm" value={crew.storm}/>
          <ItemTooltipCrewTolerance type="deadperson" value={crew.deadperson}/>
        </div>
        <ItemSources id={item.id}/>
      </div>
    </div>
  );
}

function ItemTooltipTownCrew({ item }) {
  const data = Data.items[item.id];
  const crew = Data.townCrew[data.townCrew];
  return (
    <div className={`lap-GameTooltip lap-item-${Data.itemGrades[crew.grade].color}`}>
      <div className="lap-item-title">{crew.name}</div>
      <div className="lap-item-body">
        <div className="lap-crew-header">
          <div className="lap-crew-icon">
            <div style={{backgroundImage: `url(${getIconPath(crew.icon)}`}}/>
          </div>
          <div className="lap-crew-header-info">
            <div className="lap-crew-tier">
              <span className={`la-color-${Data.itemGrades[crew.grade].color}`}>{_F(townCrewGrades[crew.grade])}</span>
              <span className="lap-crew-acquisition">{_F('sys.tooltip.target_not_acquired')}</span>
            </div>
            {!!shipTypes[crew.ship] && (
              <div className="lap-crew-ship">
                {_F('sys.tooltip.fam_dispatch_available_ship_desc', _F(shipTypes[crew.ship]))}
              </div>
            )}
            <div className="lap-crew-for-town">{_F('sys.tooltip.fam_dispatch_crew_fam_desc')}</div>
          </div>
        </div>
        <div className="lap-crew-story">{formatHtml(crew.comment)}</div>
        {!!townCrewHardshipMain[crew.hardshipMain] && (
          <div className="lap-crew-hardship">
            <div className={`lap-crew-hardship-icon lap-crew-hardship-main-${crew.hardshipMain}`}/>
            <div className="lap-crew-hardship-info">
              <p>{_F('sys.tooltip.fam_dispatch_hardship_main', _F(townCrewHardshipMain[crew.hardshipMain]))}</p>
              {!!crew.overcomeMain && <p>{_F('sys.tooltip.fam_dispatch_hardship_main_overcome', crew.overcomeMain)}</p>}
            </div>
          </div>
        )}
        {!!townCrewHardshipSub[crew.hardshipSub] && (
          <div className="lap-crew-hardship">
            <div className={`lap-crew-hardship-icon lap-crew-hardship-sub-${crew.hardshipSub}`}/>
            <div className="lap-crew-hardship-info">
              <p>{_F('sys.tooltip.fam_dispatch_hardship_sub', _F(townCrewHardshipSub[crew.hardshipSub]))}</p>
              {!!crew.overcomeSub && <p>{_F('sys.tooltip.fam_dispatch_hardship_sub_overcome', crew.overcomeSub)}</p>}
            </div>
          </div>
        )}
        <ItemSources id={item.id}/>
      </div>
    </div>
  );
}

function ItemBoxItem({ item, showCount }) {
  const data = Data.items[item.item] || Data.money[item.money];
  if (!data) return null;
  return (
    <div className="lap-item-content">
      <div className={`lap-item-content-icon lap-item-background lap-item-${Data.itemGrades[data.grade || 0].color}`}>
        <ItemIcon item={data}/>
      </div>
      <div className="lap-item-content-info">
        <p className={`lap-item-content-name la-color-${Data.itemGrades[data.grade || 0].color}`}>{data.name}</p>
        {!!(showCount && item.min >= item.max) && <p>{_F('sys.tip.dripmin_count', <ColorNumber value={item.min}/>)}</p>}
        {!!(showCount && item.min < item.max) && <p>{_F('sys.tip.dripminmax_count', <ColorNumber value={item.min}/>, <ColorNumber value={item.max}/>)}</p>}
      </div>
    </div>
  );
}

function ItemBoxContents({ item }) {
  const data = Data.items[item.id];
  if (!data.box || !data.box.contents.length) return null;
  return (
    <div className="lap-item-contents">
      <div>{formatHtml(data.box.desc)}</div>
      {data.box.contents.slice(0, 10).map((item, index) => <ItemBoxItem item={item} key={index} showCount={data.box.showCount}/>)}
      {data.box.contents.length > 10 && (
        <div className="lap-item-contents-more">{_F('sys.ingameshop.searched_qnty_besides', data.box.contents.length - 10)}</div>
      )}
    </div>
  );
}

function ItemSocialStats({ item }) {
  const data = Data.items[item.id];
  if (!data.social) return null;
  return (
    <div className="lap-item-group lap-item-social">
      <div className="lap-stats-header">
        {_F('sys.tip.tendency_info')}
      </div>
      {Object.entries(data.social).map(([stat, value]) => {
        const info = Data.socialStats[stat];
        if (!info) return null;
        return (
          <div key={stat}>{_F('sys.tip.tooltip_tendency',
            <span className={`lap-SocialIcon lap-social-${stat}`}/>, _F(info.tip), value)}</div>
        );
      })}
    </div>
  );
}

export function CardTooltip({ id, awake }) {
  const card = Data.cards[id];
  if (!card) return <div className="lap-GameTooltip">Unknown card</div>;
  return (
    <div className={`lap-GameTooltip lap-item-card lap-item-${Data.itemGrades[card.grade - 1].color}`}>
      <div className="lap-item-title">{_F('sys.tooltip.card_name', card.name)}</div>
      <div className="lap-item-body">
        <ItemCardArt id={id} awake={awake}/>
        {!!card.desc && <p className="lap-item-description">{formatHtml(card.desc)}</p>}
        <ItemSources id={card.item}/>
      </div>
    </div>
  );
}

export default function ItemTooltip({ item }) {
  if (Data.crew[item.id]) return <ItemTooltipCrew item={item}/>;
  const data = Data.items[item.id];
  if (!data) return <div className="lap-GameTooltip">Unknown item</div>;
  if (Data.townCrew[data.townCrew]) return <ItemTooltipTownCrew item={item}/>;
  if (data.card) return <CardTooltip id={data.card} awake={item.awake}/>;
  return (
    <div className={`lap-GameTooltip lap-item-${Data.itemGrades[data.grade].color}`}>
      <ItemTitle item={item}/>
      <div className="lap-item-body">
        <ItemHeader item={item}/>
        {!!data.class && <p>{_F(Data.playerClasses[data.class]?.name)}{_F('sys.tooltip.only_class')}</p>}
        <ItemBinding item={item}/>
        {!!data.tradeLimitLevel && <p>{_F('sys.tooltip.item_level_limit_enable', data.tradeLimitLevel)}</p>}
        <ItemStaticStats item={item}/>
        <ItemBonusStats item={item}/>
        <ItemRandomStats item={item}/>
        <ItemSocialStats item={item}/>
        <ItemTripod item={item}/>
        <ItemEngravingBook item={item}/>
        <ItemSkillRune item={item}/>
        <ItemGems item={item}/>
        <ItemGemEffects item={item}/>
        <ItemEngraving item={item}/>
        <ItemEngravingExtra item={item}/>
        <ItemBracelet item={item}/>
        <ItemSet item={item}/>
        <ItemDescription item={item}/>
        <ItemAssembly item={item}/>
        <ItemRestrictions item={item}/>
        <ItemEngravingBookEffect item={item}/>
        <ItemBoxContents item={item}/>
        <ItemSources id={item.id}/>
        <ItemDurability item={item}/>
      </div>
    </div>
  );
}

export function SocialActionTooltip({ id }) {
  const data = Data.socialAction[id];
  if (!data) return <div className="lap-GameTooltip">Unknown action</div>;
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-title">{data.name}</div>
      <div className="lap-item-body">
        <div className="lap-item-header">
          <div className="lap-skill-icon" style={data.icon ? {backgroundImage: `url(${getIconPath(data.icon)}`} : null}/>
          <div className="lap-item-header-info"></div>
        </div>
        <div className="lap-item-group">{formatHtml(data.desc)}</div>
        {!!data.unlock && <div className="lap-item-group">{formatHtml(data.unlock)}</div>}
      </div>
    </div>
  );
}

function formatTimeMusic(time) {
  if (time < 60 * 1000) {
    return _F('sys.music.ui_massage_second', Math.round(time / 1000));
  } else if (time < 60 * 60 * 1000) {
    return _F('sys.music.ui_massage_minute', Math.round(time / 60 / 1000));
  } else {
    return _F('sys.music.ui_massage_hour', Math.round(time / 60 / 60 / 1000));
  }
}

export function MusicTooltip({ id }) {
  const data = Data.music[id];
  if (!data) return <div className="lap-GameTooltip">Unknown music</div>;
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-title">{data.name}</div>
      <div className="lap-item-body">
        <div className="lap-item-header">
          <div className="lap-skill-icon" style={data.icon ? {backgroundImage: `url(${getIconPath(data.icon)}`} : null}/>
          <div className="lap-item-header-info">
            {!!data.cooldown && <div className="lap-item-cooldown">{_F('sys.music.ui_massage_cooldown', formatTimeMusic(data.cooldown))}</div>}
          </div>
        </div>
        <div className="lap-item-group">{formatHtml(data.desc)}</div>
        {!!data.unlock && <div className="lap-item-group">{formatHtml(data.unlock)}</div>}
      </div>
    </div>
  );
}

const lifeSkills = ['mining', 'lumbering', 'hunting', 'herbalism', 'fishing', 'archaeology'];
function IslandLifeSkills({ data }) {
  if (!lifeSkills.some(sk => data[sk])) return null;
  return (
    <div className="lap-item-group">
      <div>
        {_F('sys.tooltip.life_spawn_information', lifeSkills.map(sk => !!data[sk] && <div key={sk} className={`lap-life-skill lap-life-skill-${sk}`}/>))}
      </div>
      {data.lifeTier > 1 && <div>{_F('sys.life.exist_life_tier_continent', data.lifeTier)}</div>}
    </div>
  );
}

export function IslandTooltip({ id }) {
  const data = Data.zone[id];
  if (!data || !data.island) return <div className="lap-GameTooltip">Unknown island</div>;
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-title">{data.name}</div>
      <div className="lap-item-body">
        <div className="lap-item-header">
          <div className="lap-skill-icon" style={data.island.icon ? {backgroundImage: `url(${getIconPath(data.island.icon)}`} : null}/>
          <div className="lap-item-header-info">
            <div className="lap-island-token">
              {_F('sys.tooltip.island_heart')} : {_F(data.island.token ? 'sys.tooltip.island_heart_acquired' : 'sys.tooltip.island_heart_empty')}
            </div>
            {!!data.island.mokoko && (
              <div className="lap-island-mokoko">
                {_F('sys.tooltip.island_mococo_gain', data.island.mokoko, data.island.mokoko)}
              </div>
            )}
            {!!data.cooldown && <div className="lap-item-cooldown">{_F('sys.music.ui_massage_cooldown', formatTimeMusic(data.cooldown))}</div>}
          </div>
        </div>
        <div className="lap-item-group">
          <div>{_F('sys.tooltip.island_entry_balance_level')} : {data.island.gs || _F('sys.tooltip.island_entry_balance_level_default')}</div>
          <div>{_F('sys.tooltip.island_entry_time')} : {data.island.entry ? formatHtml(data.island.entry) : _F('sys.tooltip.island_entry_time_default')}</div>
          {!!data.island.warp && <div>{_F('sys.bm_warp.use_condition_not_use_tooltip')}</div>}
        </div>
        {!!data.island.desc && (
          <div className="lap-item-group">
            <p>{formatHtml(data.island.desc)}</p>
          </div>
        )}
        <IslandLifeSkills data={data.island}/>
      </div>
    </div>
  );
}

export function ItemSetTooltip({ id }) {
  const data = Data.itemSet[id];
  if (!data) return <div className="lap-GameTooltip">Unknown item set</div>;
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-title">{data.name}</div>
      <div className="lap-item-body">
        <ItemSetEffects set={data}/>
      </div>
    </div>
  );
}
